/**
 * useInquiryHydration — map a CustInq response to a RaoPerson.
 *
 * Implements the read-side of docs/port-design/people-form-port.md:
 *   §1 middle-name handling  → splitFirstMiddle() on the AI/Core first name
 *   §4 CD address inversion  → primary slot (f9-12) may be mailing when the
 *                              secondary slot (f86-89) is populated
 *   §6 employment inference  → derive `employmentStatus` from Field 141 / 313
 *
 * All other fields pass through — phones (Home/Cell/Bus), email, identifiers,
 * employer/occupation/NAICS. The existing v3 `parseCustInq` utility handles
 * the jXRL unwrap; we just layer the port-specific transforms on top.
 */
import { useCallback } from 'react';
import type { CustInqResult } from '@retail-ops/shared-types';
import type { DocumentRef } from '@retail-ops/api-client';
import { parseCustInq, type ParsedCustInq } from '@/utils/custInqParser';
import { useApiClient } from '@/contexts/ApiClientContext';
import type { RaoPerson } from '../../../types';
import { splitFirstMiddle } from '../../../utils/splitFirstMiddle';
import type { AttachedDoc, AttachedDocSource } from './attachedDocs';

export interface InquiryHydrationInput {
  /**
   * Raw CustInq response — pass the *whole envelope* you got back from
   * `client.accounts.custInq(cifId)`, not just the `.Data`/`.data` slot.
   * `parseCustInq` (called below via `parseCustInq(input.data, ...)`)
   * unwraps both PascalCase (`Data`) and camelCase (`data`) envelopes via
   * its internal `extractDataNode`. The Function host serializes with
   * `JsonNamingPolicy.CamelCase`, so reading `.Data` directly returns
   * `undefined` even though the typed `DomainResponseEnvelope<T>` claims
   * PascalCase keys. See PersonSearchBar.tsx for the matching note.
   */
  data: CustInqResult | Record<string, unknown> | unknown;
  /** CIF id echo — used by parseCustInq's empty-result fallback. */
  cifId: string;
}

/**
 * Infer the UI-only `employmentStatus` value from Core Director's field 141
 * (employer) and field 313 (occupation). Mirrors §6's `inferEmploymentStatus`.
 *
 * Returns `null` when neither field yields enough signal to pick a status —
 * callers should leave the select blank and let the officer pick manually.
 */
export function inferEmploymentStatus(p: {
  employer?: string;
  occupation?: string;
}): string | null {
  const emp = (p.employer ?? '').trim();
  const occ = (p.occupation ?? '').trim();

  if (emp === 'Minor' && occ === 'Minor') return 'Minor';
  if (emp === 'Homemaker' && occ === 'Homemaker') return 'Homemaker';
  if (/^Retired/i.test(emp)) return 'Retired';
  if (/^Unemployed/i.test(emp)) return 'Unemployed';
  if (occ === 'Student') return 'Student';

  if (emp && occ) return 'Employed';
  return null;
}

/**
 * §4 — Core Director address slots (Bill 2026-09-29): fields 9-12 are ALWAYS
 * the mailing address; UDF fields 86-89 ("2nd Address") are the PHYSICAL
 * address and are populated only when it differs; blank 86-89 means 9-12 is
 * both. This is the read-side of that rule for CustInq -> RaoPerson.
 *
 * 86-89 arrive in x_BusDetail (parsed as `secondary*` by custInqParser) and
 * ONLY when the CustInq was requested with includeUdf — useCustomerPick does.
 * Without it the secondary is blank and the person hydrates as single-address,
 * which is also the normal case. Until 2026-09-29 this looked for a
 * CustDetail.SecondaryAddr / Addr2 element that Core never sends, so the
 * inversion never fired and a Core-born customer with a PO Box hydrated with
 * the PO Box as their physical address.
 */
function applyAddressInversion(
  person: Partial<RaoPerson>,
  parsed: ParsedCustInq,
): Partial<RaoPerson> {
  const secondary: SecondaryAddr = {
    addr: parsed.secondaryAddr,
    city: parsed.secondaryCity,
    state: parsed.secondaryState,
    zip: parsed.secondaryZip,
  };

  if (isSecondaryEmpty(secondary)) {
    // No secondary slot -> primary address is both physical + mailing.
    const next: Partial<RaoPerson> = { ...person };
    if (parsed.addr) next.addr = parsed.addr;
    if (parsed.city) next.city = parsed.city;
    if (parsed.state) next.state = parsed.state;
    if (parsed.zip) next.zip = parsed.zip;
    const prevExtra = (next.extra ?? {}) as Record<string, unknown>;
    next.extra = { ...prevExtra, useMailingAddr: false };
    return next;
  }

  // Secondary populated -> primary (9-12) is MAILING, secondary (86-89) is PHYSICAL.
  const next: Partial<RaoPerson> = { ...person };
  next.addr = secondary.addr;
  next.city = secondary.city;
  next.state = secondary.state;
  next.zip = secondary.zip;
  if (parsed.addr) next.mailAddr = parsed.addr;
  if (parsed.city) next.mailCity = parsed.city;
  if (parsed.state) next.mailState = parsed.state;
  if (parsed.zip) next.mailZip = parsed.zip;
  const prevExtra = (next.extra ?? {}) as Record<string, unknown>;
  next.extra = { ...prevExtra, useMailingAddr: true };
  return next;
}

interface SecondaryAddr {
  addr: string;
  city: string;
  state: string;
  zip: string;
}

function isSecondaryEmpty(s: SecondaryAddr): boolean {
  return !s.addr && !s.city && !s.state && !s.zip;
}

// -----------------------------------------------------------------------------
// Public hook
// -----------------------------------------------------------------------------

export interface UseInquiryHydrationResult {
  /**
   * Convert a CustInq response into a RaoPerson patch suitable for
   * `setState` merging with a base person row. Returned shape is fully
   * populated — caller can use `...prev, ...hydrate(inq).patch` to apply.
   */
  hydrate: (input: InquiryHydrationInput) => RaoPerson;
}

/**
 * Thin hook — the transforms are pure functions so the "hook" is really just
 * a stable callback wrapper. Returning it through a hook keeps the call-site
 * consistent with `useSaveDispatch` and `useStateGovLink` in this folder.
 */
export function useInquiryHydration(): UseInquiryHydrationResult {
  const hydrate = useCallback((input: InquiryHydrationInput): RaoPerson => {
    const parsed = parseCustInq(input.data, input.cifId);
    const split = splitFirstMiddle(parsed.firstName);

    const base: Partial<RaoPerson> = {
      role: 'primary',
      mode: 'existing',
      cifId: parsed.cifId,
      firstName: split.first || parsed.firstName,
      ...(split.middle ? { middleName: split.middle } : parsed.middleName ? { middleName: parsed.middleName } : {}),
      lastName: parsed.lastName,
      fullName: [split.first || parsed.firstName, parsed.lastName].filter(Boolean).join(' '),
      ...(parsed.suffix ? { suffix: parsed.suffix } : {}),
      ...(parsed.comName ? { name2: parsed.comName } : {}),
      ...(parsed.dob ? { dob: parsed.dob } : {}),
      ...(parsed.taxId ? { taxId: parsed.taxId } : {}),
      ...(parsed.tinCode ? { tinCode: parsed.tinCode as RaoPerson['tinCode'] } : {}),
      ...(parsed.customerType ? { customerType: parsed.customerType as RaoPerson['customerType'] } : {}),
      ...(parsed.insiderCode ? { insiderCode: parsed.insiderCode } : {}),
      ...(parsed.gender === 'Male' || parsed.gender === 'Female' ? { gender: parsed.gender } : {}),

      // Contact
      ...(parsed.phoneHome ? { phoneHome: parsed.phoneHome } : {}),
      ...(parsed.phoneCell ? { phoneCell: parsed.phoneCell } : {}),
      ...(parsed.phoneBus ? { phoneBus: parsed.phoneBus } : {}),
      ...(parsed.email ? { email: parsed.email } : {}),

      // Employment (raw fields — address inversion applied next)
      ...(parsed.employer ? { employer: parsed.employer } : {}),
      ...(parsed.occupation ? { occupation: parsed.occupation } : {}),
      ...(parsed.naicsCode ? { naicsCode: parsed.naicsCode } : {}),

      // CIP / ID-verify (Field 254-258) — pre-fix these were extracted by
      // parseCustInq into ParsedCustInq.{idTypeCode, idNumber, idState,
      // idIssueDate, idExpiryDate} but the RAO hydration adapter dropped
      // them on the floor (RCM's custInqToRaoPerson did include them, so the
      // CIF Edit view rendered them — but RAO's Review-and-Add modal didn't).
      // IndividualCipScanSection reads person.extra.{idType,idNumber,
      // idIssuer,idIssueDate,idExpiryDate}, so we seed them here.
      extra: {
        // Provenance — hydrated from a real CIF, so isExistingCustomer() must
        // say so. This marker was checked but never written by anything, which
        // left the predicate dependent on an LN score / OFAC scan — the very
        // artifacts this path skips — so a clean existing CIF was treated as a
        // fresh add and re-prompted for an ID already on file (Bill 2026-07-15).
        // NOT conditional: provenance is true regardless of which CIP fields
        // came back (a CIF with no ID on file is still an existing CIF, and
        // derivePrimaryIdGating already returns 'required' for that case).
        cifSourceMode: 'existing-cif-search-result',
        ...(parsed.idTypeCode ? { idType: parsed.idTypeCode } : {}),
        ...(parsed.idNumber ? { idNumber: parsed.idNumber } : {}),
        ...(parsed.idState ? { idIssuer: parsed.idState } : {}),
        ...(parsed.idIssueDate ? { idIssueDate: parsed.idIssueDate } : {}),
        ...(parsed.idExpiryDate ? { idExpiryDate: parsed.idExpiryDate } : {}),
      },

      // Citizenship (condensed) — Field 356, populated for every CIF.
      ...(parsed.countryOfCitizenship ? { countryOfCitizenship: parsed.countryOfCitizenship } : {}),

      // BACKLOG-0512 — normalize citizenshipStatus at hydration boundary so
      // the modal-level `local` state IS the single source of truth that
      // every downstream consumer (PdwStatusBadges, CitizenshipPicker, Origin
      // tab indicator, IndividualNraSection, useIndividualDerivation, gates)
      // reads. Without this, an existing CIF with no Alien_CType block left
      // citizenshipStatus undefined; the picker papered over it with a
      // `?? 'US Citizen'` display fallback while the badges read the raw
      // undefined and showed "pending" — divergent reads that LOOKED like
      // state shadows. Default to 'US Citizen' (the 99% case for PCB CIFs);
      // NRA branch below overrides when Alien_CType is present.
      citizenshipStatus: 'US Citizen' as const,

      // Alien_CType (NRA path) — only populated when CustInq returned
      // CustDetail.TaxDetail.Alien_CType. parseCustInq's pickAlienCType
      // handles the absent-block case (returns blanks); the conditional
      // spreads below ensure we don't overwrite a US-citizen RaoPerson
      // shell with a no-op of empty strings (which would mark `dirty`).
      ...(parsed.alienCitizenshipCountry
        ? { countryOfCitizenship: parsed.alienCitizenshipCountry,
            citizenshipStatus: 'Non-Resident Alien' as const }
        : {}),
      ...(parsed.alienTaxResidencyCountry ? { countryOfTaxResidency: parsed.alienTaxResidencyCountry } : {}),
      ...(parsed.alienVisaType            ? { visaType: parsed.alienVisaType }                      : {}),
      ...(parsed.alienVisaExpDt           ? { visaExpDt: parsed.alienVisaExpDt }                    : {}),
      ...(parsed.alienI94Number           ? { i94Number: parsed.alienI94Number }                    : {}),
      ...(parsed.alienI94ExpDt            ? { i94ExpDt: parsed.alienI94ExpDt }                      : {}),
      ...(parsed.alienPassportNumber      ? { foreignPassportNumber: parsed.alienPassportNumber }   : {}),
      ...(parsed.alienPassportCountry     ? { foreignPassportCountry: parsed.alienPassportCountry } : {}),
      ...(parsed.alienFornTaxId           ? { fornTaxId: parsed.alienFornTaxId }                    : {}),
      ...(parsed.alienW8Type              ? { alienW8Type: parsed.alienW8Type as RaoPerson['alienW8Type'] } : {}),
      ...(parsed.alienLastW8Dt            ? { alienLastW8Dt: parsed.alienLastW8Dt }                 : {}),
      ...(parsed.alienW8ExpDt             ? { alienW8ExpDt: parsed.alienW8ExpDt }                   : {}),
      ...(parsed.alienIRSRptType          ? { alienIRSRptType: parsed.alienIRSRptType as RaoPerson['alienIRSRptType'] } : {}),
      ...(parsed.alienIRSRecipCode        ? { irsRecipCode: parsed.alienIRSRecipCode }              : {}),
      ...(parsed.alienIRSExmpCode         ? { irsExmpCode: parsed.alienIRSExmpCode }                : {}),
      ...(parsed.alienResAddr             ? { foreignResidenceAddr: parsed.alienResAddr }           : {}),
      ...(parsed.alienResCity             ? { foreignResidenceCity: parsed.alienResCity }           : {}),
      ...(parsed.alienResState            ? { foreignResidenceState: parsed.alienResState }         : {}),
      ...(parsed.alienResPostal           ? { foreignResidencePostal: parsed.alienResPostal }       : {}),
      ...(parsed.alienResCountry          ? { foreignResidenceCountry: parsed.alienResCountry }     : {}),

      cipStatus: 'pending',
      dirty: false,
    };

    // §6 — infer UI-only employmentStatus
    const inferred = inferEmploymentStatus({
      employer: parsed.employer,
      occupation: parsed.occupation,
    });
    if (inferred) base.employmentStatus = inferred;

    // §4 — address inversion
    const withAddresses = applyAddressInversion(base, parsed);

    // Cast — all required RaoPerson fields are set by now.
    return withAddresses as RaoPerson;
  }, []);

  return { hydrate };
}

// -----------------------------------------------------------------------------
// Edit-mode document hydration (Slot-4 T5)
//
// CustInq carries the CIP scalar fields (idType/idNumber/…) but NOT the
// captured document images — those live in the v3 Retail Document Library
// (`ao_Documents`, exposed via `GET /v3/documents/list?cifId=`). Without this,
// an existing CIF opened in Edit shows zero captured docs (so the Identity-
// Verified badge can never count the photo-ID) even though the images exist in
// storage. We map each `DocumentRef` to an `AttachedDoc` REFERENCE (Bill Q3
// default — no inline blob; `content()` lazy-fetches bytes for preview).
// -----------------------------------------------------------------------------

/**
 * Map a v3 Document Library row to the AttachedDoc reference shape. The
 * `documentId` becomes `spItemId` (the canonical reference), `relativePath`
 * becomes `fileUrl`. `imageDataUrl` is intentionally empty — the bytes are
 * fetched on demand via `apiClient.retail.documents.content(spItemId)`.
 */
export function documentRefToAttachedDoc(ref: DocumentRef): AttachedDoc {
  const source: AttachedDocSource =
    ref.category === 'fsr_upload' || ref.category === 'customer_upload' ? 'upload' : 'add-existing';
  return {
    docCode: ref.docCode ?? ref.regulatedArtifactKind ?? ref.documentId,
    source,
    filename: ref.fileName,
    contentType: ref.contentType,
    sizeBytes: ref.sizeBytes,
    imageDataUrl: '',
    fileUrl: ref.relativePath ?? '',
    capturedAt: ref.uploadedAt,
    spItemId: ref.documentId,
  };
}

export interface UseAttachedDocsHydrationResult {
  /**
   * Fetch the prior captured documents for a CIF from the v3 Document Library
   * and map them to AttachedDoc references. Tolerant: returns `[]` on any
   * error / SP-unavailable so Edit never fails to open over a doc-fetch hiccup.
   */
  fetchAttachedDocsForCif: (cifId: string) => Promise<ReadonlyArray<AttachedDoc>>;
}

/**
 * Companion hook to {@link useInquiryHydration} for the async doc-library
 * leg of Edit-mode hydration. Kept separate from `hydrate` (which is a pure
 * sync transform) because the document fetch is an API round-trip.
 */
export function useAttachedDocsHydration(): UseAttachedDocsHydrationResult {
  const apiClient = useApiClient();
  const fetchAttachedDocsForCif = useCallback(
    async (cifId: string): Promise<ReadonlyArray<AttachedDoc>> => {
      const trimmed = (cifId ?? '').trim();
      if (!trimmed) return [];
      try {
        const resp = await apiClient.retail.documents.list({ cifId: trimmed });
        if (!resp.success || !Array.isArray(resp.data)) return [];
        return resp.data.map(documentRefToAttachedDoc);
      } catch {
        // SP_UNAVAILABLE / NOT_IMPLEMENTED / network — degrade silently; the
        // CIP scalar fields still hydrated, the doc tiles just start empty.
        return [];
      }
    },
    [apiClient],
  );
  return { fetchAttachedDocsForCif };
}
